/**
 * @module store
 * @category Gerenciador de estados
 * @summary Módulo do estado global do sistema.
 *
 * @description
 * Expõe uma instância de {@link external:Store Store}, contendo o estado
 * global do sistema e seus `getters` e `mutations`.
 *
 * @requires module:store/getters
 * @requires module:store/mutations
 */
import Vue from "vue";
import Vuex from "vuex";

import * as getters from "./getters";
import * as mutations from "./mutations";

Vue.use(Vuex);

/**
 * Estrutura com as opções utilizadas pelo gerenciador de estados do sistema
 * ({@link external:Vuex Vuex}).
 * @type {object}
 * @property {object} state - Estado global do sistema.
 * @property {object} state.authenticationData - Dados de autenticação do
 * usuário logado.
 * @property {string} state.authenticationMode - Modo de autenticação
 * utilizado.
 * @property {Array<object>} state.dowloadedModulos - _Array_ com os códigos dos
 * módulos já baixados pelo sistema.
 * @property {object} state.infoCards - Objeto cujas chaves são os `id`s dos
 * _infocard_s do sistema e os valores são seus metadados. Utilizado para
 * restaurar/reexibir os _infocard_s por reatividade do {@link external:Vue Vue}.
 * @property {Array<string>} state.infoCardIds - _Array_ com os `id`s dos
 * _infocard_s do sistema, exceto dos módulos.
 * @property {boolean} state.loadingModulo - Indica se um módulo está sendo
 * carregado pelo sistema ou não.
 * @property {object} state.modulo - Metadados do módulo ativo.
 * @property {Array<object>} state.moduloMenu - Itens de menu do módulo
 * ativo.
 * @property {Array<object>} state.modulos - _Array_ com os metadados dos
 * módulos registrados no sistema.
 * @property {object} state.permissions - Objetos com as permissões do usuário
 * logado.
 * @property {string} state.ticket - Ticket do {@link https://cas.ufrj.br CAS} da UFRJ.
 * @property {object} state.user - Objeto com os dados do usuário logado.
 * @property {object} getters - {@link module:store/getters}.
 * @property {object} mutations - {@link module:store/mutations}.
 * @constant
 */
const storeOptions = {
  state: {
    authenticationData: null,
    authenticationMode: null,
    downloadedModulos: [],
    infoCards: {},
    infoCardIds: [],
    loadingModulo: false,
    modulo: null,
    moduloMenu: [],
    modulos: [],
    permissions: {},
    ticket: null,
    user: null
  },
  getters,
  mutations
};

export default new Vuex.Store(storeOptions);
